import axios, { AxiosResponse } from 'axios';

import type { Store } from '@reduxjs/toolkit';
import type { AxiosRequestConfig, AxiosInstance } from 'axios';
import type { ApiResponse } from './types';

import { OIDC_USER } from '@/config/variables';
import { clearAuth } from '@/store/slices/authSlice';
import { API_BASE_URL } from '@/config/variables';

const instance: AxiosInstance = axios.create({
  withCredentials: true,
  headers: { 'Content-Type': 'application/json; charset=utf-8' },
});

const getAccessToken = (): string => {
  const oidcUser = localStorage.getItem(OIDC_USER);
  if (oidcUser === null) return '';

  return JSON.parse(oidcUser).access_token;
};

export const setupInterceptors = (store: Store): void => {
  instance.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        // error.response is missing if a request is cancelled -> error.message
        // clearAuth(): clearing state.auth.user will
        // automatically redirect to logout
        store.dispatch(clearAuth());
        window.location.href = '/api/v3/auth/login';
      }
      return Promise.reject(error);
    },
  );
};

const getMergedConfig = (config?: AxiosRequestConfig): AxiosRequestConfig => {
  return {
    ...config,
    baseURL: config?.baseURL ?? API_BASE_URL,
    headers: {
      ...config?.headers,
      Authorization: `Bearer ${getAccessToken()}`,
    },
  } as AxiosRequestConfig;
};

export const v3Get = <T>(url: string, config?: AxiosRequestConfig): ApiResponse<T> =>
  instance.get<T>(url, getMergedConfig(config));

export const v3Remove = (url: string, config?: AxiosRequestConfig): ApiResponse =>
  instance.delete(url, getMergedConfig(config));

export const v3Post = <T, Data = unknown>(url: string, data?: Data, config?: AxiosRequestConfig): ApiResponse<T> =>
  instance.post<T>(url, data, getMergedConfig(config));

export const v3PostWithResponseInterceptor = <T, Data = unknown>(
  url: string,
  responseInterceptor: (value: AxiosResponse<unknown>) => AxiosResponse<unknown> | Promise<AxiosResponse<unknown>>,
  data?: Data,
  config?: AxiosRequestConfig,
): ApiResponse<T> => {
  let interceptorId = -1;
  interceptorId = instance.interceptors.response.use(responseInterceptor);
  const response = instance.post<T>(url, data, getMergedConfig(config));
  instance.interceptors.response.eject(interceptorId);
  return response;
};

export const v3Put = <T>(url: string, data?: unknown, config?: AxiosRequestConfig): ApiResponse<T> =>
  instance.put<T>(url, data, getMergedConfig(config));

export const v3Patch = <T>(url: string, data?: unknown, config?: AxiosRequestConfig): ApiResponse<T> =>
  instance.patch<T>(url, data, getMergedConfig(config));
